import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Video, Plus, Trash2, Play, Heart, Eye, X } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SellerReels() {
    const { t } = useLang();
    const [reels, setReels] = useState([]);
    const [products, setProducts] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ video_url: "", thumbnail_url: "", caption: "", product_id: "" });
    const [uploading, setUploading] = useState(false);
    const [publishing, setPublishing] = useState(false);

    const load = () => api.get("/reels/mine").then((r) => setReels(r.data));
    useEffect(() => {
        load();
        api.get("/seller/products").then((r) => setProducts(r.data));
    }, []);

    const uploadVideo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/uploads/video", fd);
            setForm((f) => ({ ...f, video_url: data.url }));
            toast.success("✓");
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
        finally { setUploading(false); }
    };

    const uploadThumb = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/uploads/image", fd);
            setForm((f) => ({ ...f, thumbnail_url: data.url }));
        } catch { toast.error("Upload failed"); }
    };

    const publish = async (e) => {
        e.preventDefault();
        if (!form.video_url) { toast.error(t("uploadVideo")); return; }
        setPublishing(true);
        try {
            const payload = { ...form };
            if (!payload.product_id) delete payload.product_id;
            await api.post("/reels", payload);
            toast.success(t("reelPublish") + " ✓");
            setForm({ video_url: "", thumbnail_url: "", caption: "", product_id: "" });
            setShowForm(false);
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
        finally { setPublishing(false); }
    };

    const del = async (id) => {
        if (!confirm("?")) return;
        await api.delete(`/reels/${id}`);
        toast.success("✓");
        load();
    };

    return (
        <Layout>
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
                <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                    <h1 className="text-3xl md:text-4xl font-black text-espresso">{t("myReels")}</h1>
                    <Button onClick={() => setShowForm(true)} className="rounded-full bg-terracotta text-white" data-testid="add-reel-btn">
                        <Plus className="w-4 h-4 me-2" />{t("addReel")}
                    </Button>
                </div>

                <p className="text-sm text-muted-foreground mb-6">{t("reelHint")}</p>

                {showForm && (
                    <form onSubmit={publish} className="bg-white border border-border rounded-3xl p-6 md:p-8 mb-8 space-y-5 relative" data-testid="reel-form">
                        <button type="button" onClick={() => setShowForm(false)} className="absolute top-4 end-4 p-1 rounded-full hover:bg-surface" aria-label="Close">
                            <X className="w-5 h-5" />
                        </button>

                        <div>
                            <Label>{t("uploadVideo")}</Label>
                            <label className="mt-1.5 flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-2xl p-6 cursor-pointer hover:border-terracotta transition-colors" data-testid="upload-video-dropzone">
                                <Video className="w-5 h-5 text-terracotta" />
                                <span className="text-sm font-bold text-espresso">{form.video_url ? "✓" : t("upload")}</span>
                                <input type="file" accept="video/*" onChange={uploadVideo} className="hidden" data-testid="video-file-input" />
                            </label>
                            {uploading && <div className="text-xs text-muted-foreground mt-2">...</div>}
                            {form.video_url && (
                                <video src={form.video_url.startsWith("http") ? form.video_url : `${process.env.REACT_APP_BACKEND_URL}${form.video_url}`} controls className="mt-3 w-full max-w-xs rounded-xl" />
                            )}
                        </div>

                        <div>
                            <Label>Thumbnail (optional)</Label>
                            <input type="file" accept="image/*" onChange={uploadThumb} className="mt-1.5 text-xs" data-testid="thumb-file-input" />
                            {form.thumbnail_url && <img src={fileUrl(form.thumbnail_url)} alt="" className="mt-2 w-24 h-24 object-cover rounded-lg" />}
                        </div>

                        <div>
                            <Label>{t("reelCaption")}</Label>
                            <Textarea rows={2} value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="mt-1.5" data-testid="reel-caption-input" />
                        </div>

                        <div>
                            <Label>{t("linkProduct")}</Label>
                            <Select value={form.product_id || "none"} onValueChange={(v) => setForm({ ...form, product_id: v === "none" ? "" : v })}>
                                <SelectTrigger className="mt-1.5" data-testid="reel-product-select"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">—</SelectItem>
                                    {products.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.title}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        <Button type="submit" disabled={publishing || !form.video_url} className="w-full rounded-full h-11 bg-terracotta text-white font-bold" data-testid="publish-reel-btn">
                            {publishing ? "..." : t("reelPublish")}
                        </Button>
                    </form>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="my-reels-grid">
                    {reels.length === 0 ? (
                        <div className="col-span-full text-center py-16 text-muted-foreground">{t("noResults")}</div>
                    ) : (
                        reels.map((r) => (
                            <Link key={r.reel_id} to={`/reels?r=${r.reel_id}`} className="group relative aspect-[9/16] rounded-2xl overflow-hidden bg-espresso border border-border" data-testid={`my-reel-${r.reel_id}`}>
                                {r.thumbnail_url ? (
                                    <img src={fileUrl(r.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <video src={r.video_url} muted className="w-full h-full object-cover" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                <Play className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 text-white/90 fill-white/90" />
                                <div className="absolute inset-x-0 bottom-0 p-3 text-white text-xs">
                                    <div className="line-clamp-2 mb-1">{r.caption}</div>
                                    <div className="flex items-center gap-3 opacity-90">
                                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{r.views || 0}</span>
                                        <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{r.likes_count || 0}</span>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); del(r.reel_id); }}
                                    className="absolute top-2 end-2 p-1.5 rounded-full bg-red-600/90 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                    data-testid={`del-reel-${r.reel_id}`}
                                >
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </Link>
                        ))
                    )}
                </div>
            </div>
        </Layout>
    );
}
